import { Link, useLocation } from "wouter";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { hasSaasReadAccess } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  User,
  LogOut,
  Settings,
  HelpCircle,
  ChevronDown,
  Bug,
  Menu,
  X,
  Shield,
  Sparkles,
  Users,
  CreditCard,
  Building2,
  Share2,
} from "lucide-react";
import teamPulseLogo from "@assets/TeamPulse Header Logo_small.webp";
import AccountSwitcher from "@/components/AccountSwitcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

const NavLink = ({
  href,
  children,
  isMobile = false,
  onClick,
}: {
  href: string;
  children: React.ReactNode;
  isMobile?: boolean;
  onClick?: () => void;
}) => {
  const [location] = useLocation();
  const isActive = location === href;

  if (isMobile) {
    return (
      <Link href={href} onClick={onClick}>
        <span
          className={`${
            isActive
              ? "bg-primary bg-opacity-10 text-primary"
              : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
          } 
          block pl-3 pr-4 py-2 text-base font-medium cursor-pointer`}
        >
          {children}
        </span>
      </Link>
    );
  }

  return (
    <Link href={href}>
      <span
        className={`${
          isActive
            ? "border-primary text-neutral-800"
            : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
        } 
        inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium cursor-pointer`}
      >
        {children}
      </span>
    </Link>
  );
};

export default function Navbar() {
  const { user, isAuthenticated, logoutMutation } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [location] = useLocation();

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 sm:h-20 lg:h-24">
          <div className="flex items-center">
            <Link href="/">
              <div
                className="flex-shrink-0 flex items-center cursor-pointer"
                data-testid="link-home-logo"
              >
                <img
                  src={teamPulseLogo}
                  alt="TeamPulse®"
                  width={168}
                  height={112}
                  className="h-12 sm:h-16 lg:h-20 w-auto"
                />
              </div>
            </Link>
            <div className="hidden md:ml-6 md:flex md:space-x-8">
              {/* Any user with SaaS-side access gets a top-level SaaS Dashboard link */}
              {isAuthenticated && hasSaasReadAccess(user) && (
                <NavLink href="/all-organizations">SaaS Dashboard</NavLink>
              )}
              {isAuthenticated && user?.companyId && (
                <>
                  {/* Users only see My Reports and Fill Out a Report */}
                  {user?.role === "user" && (
                    <>
                      <NavLink href="/my-reports">My Reports</NavLink>
                      <NavLink href="/weekly-report">Fill Out a Report</NavLink>
                    </>
                  )}

                  {/* Owners, Managers, Supervisors, Admins, and Super Admins see Team Reports, My Reports, Fill Out a Report, and Admin */}
                  {/* isSuperAdmin only grants admin nav when the active company role is not 'user' */}
                  {(user?.role === "owner" ||
                    user?.role === "manager" ||
                    user?.role === "supervisor" ||
                    user?.role === "admin" ||
                    (user?.isSuperAdmin && user?.role !== "user")) && (
                    <>
                      <NavLink href="/team-reports">Team Reports</NavLink>
                      <NavLink href="/my-reports">My Reports</NavLink>
                      <NavLink href="/weekly-report">Fill Out a Report</NavLink>
                      <NavigationMenu>
                        <NavigationMenuList>
                          <NavigationMenuItem>
                            <NavigationMenuTrigger
                              className={`${
                                location === "/company" || location === "/saas-settings"
                                  ? "border-primary text-neutral-800"
                                  : "border-transparent text-gray-500 hover:text-gray-700"
                              } 
                                bg-transparent hover:bg-transparent data-[state=open]:bg-transparent px-1 pt-1 h-auto rounded-none border-b-2 text-sm font-medium cursor-pointer`}
                              data-testid="nav-admin-dropdown"
                              onClick={(e) => {
                                const target = e.target as HTMLElement;
                                if (!target.closest("svg") && target.tagName !== "path") {
                                  window.location.href = "/company";
                                }
                              }}
                            >
                              Admin
                            </NavigationMenuTrigger>
                            <NavigationMenuContent>
                              <div className="w-48 p-2">
                                <button
                                  onClick={() => (window.location.href = "/company?tab=team")}
                                  className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                                  data-testid="nav-admin-team-mgmt"
                                >
                                  <Users className="mr-2 h-4 w-4" />
                                  Team Mgmt
                                </button>
                                <button
                                  onClick={() => (window.location.href = "/company?tab=billing")}
                                  className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                                  data-testid="nav-admin-billing"
                                >
                                  <CreditCard className="mr-2 h-4 w-4" />
                                  Billing
                                </button>
                                <button
                                  onClick={() => (window.location.href = "/company?tab=settings")}
                                  className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                                  data-testid="nav-admin-settings"
                                >
                                  <Settings className="mr-2 h-4 w-4" />
                                  Settings
                                </button>
                                {hasSaasReadAccess(user) && (
                                  <>
                                    <button
                                      onClick={() => (window.location.href = "/saas-settings")}
                                      className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                                      data-testid="nav-admin-saas-settings"
                                    >
                                      <Building2 className="mr-2 h-4 w-4" />
                                      SaaS Settings
                                    </button>
                                  </>
                                )}
                              </div>
                            </NavigationMenuContent>
                          </NavigationMenuItem>
                        </NavigationMenuList>
                      </NavigationMenu>
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isAuthenticated ? (
              <>
                <div className="hidden sm:block">
                  <AccountSwitcher />
                </div>
                <div className="hidden sm:block">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="flex items-center gap-2 hover:opacity-80 transition-opacity focus:outline-none"
                        data-testid="button-profile-menu"
                      >
                        <span className="text-sm font-medium text-neutral-700 hidden lg:block">
                          {user?.firstName}
                        </span>
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user?.profileImageUrl || ""} alt="Profile" />
                          <AvatarFallback>
                            {user?.firstName?.charAt(0) || user?.lastName?.charAt(0) || <User />}
                          </AvatarFallback>
                        </Avatar>
                        <ChevronDown className="h-4 w-4 text-gray-500 hidden lg:block" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem
                        onClick={() => (window.location.href = "/account")}
                        data-testid="menu-item-edit-profile"
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        Edit Profile
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => (window.location.href = "/security")}
                        data-testid="menu-item-security"
                      >
                        <Shield className="mr-2 h-4 w-4" />
                        Security
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => (window.location.href = "/new-features")}
                        data-testid="menu-item-new-features"
                      >
                        <Sparkles className="mr-2 h-4 w-4" />
                        New Features
                      </DropdownMenuItem>
                      {user?.isReferralPartner && (
                        <DropdownMenuItem
                          onClick={() => (window.location.href = "/referral-dashboard")}
                          data-testid="menu-item-referral-dashboard"
                        >
                          <Share2 className="mr-2 h-4 w-4" />
                          Referral Dashboard
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => (window.location.href = "/help")}
                        data-testid="menu-item-help"
                      >
                        <HelpCircle className="mr-2 h-4 w-4" />
                        Help & Support
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => (window.location.href = "/report-bug")}
                        data-testid="menu-item-report-bug"
                      >
                        <Bug className="mr-2 h-4 w-4" />
                        Report a Bug
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => logoutMutation.mutate()}
                        disabled={logoutMutation.isPending}
                        data-testid="menu-item-logout"
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        {logoutMutation.isPending ? "Logging out..." : "Logout"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Mobile menu button */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary transition-colors"
                  data-testid="button-mobile-menu"
                  aria-expanded={mobileMenuOpen}
                  aria-label="Toggle navigation menu"
                >
                  {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </>
            ) : (
              <Button onClick={() => (window.location.href = "/auth")} size="sm">
                Log in
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isAuthenticated && mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="pt-2 pb-3 space-y-1">
            {hasSaasReadAccess(user) && (
              <NavLink href="/all-organizations" isMobile onClick={() => setMobileMenuOpen(false)}>
                SaaS Dashboard
              </NavLink>
            )}
            {user?.companyId && (
              <>
                {/* Users only see My Reports and Fill Out a Report */}
                {user?.role === "user" && (
                  <>
                    <NavLink href="/my-reports" isMobile onClick={() => setMobileMenuOpen(false)}>
                      My Reports
                    </NavLink>
                    <NavLink
                      href="/weekly-report"
                      isMobile
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Fill Out a Report
                    </NavLink>
                  </>
                )}

                {/* Owners, Managers, Supervisors, Admins, and Super Admins see Team Reports, My Reports, Fill Out a Report, and Admin */}
                {/* isSuperAdmin only grants admin nav when the active company role is not 'user' */}
                {(user?.role === "owner" ||
                  user?.role === "manager" ||
                  user?.role === "supervisor" ||
                  user?.role === "admin" ||
                  (user?.isSuperAdmin && user?.role !== "user")) && (
                  <>
                    <NavLink href="/team-reports" isMobile onClick={() => setMobileMenuOpen(false)}>
                      Team Reports
                    </NavLink>
                    <NavLink href="/my-reports" isMobile onClick={() => setMobileMenuOpen(false)}>
                      My Reports
                    </NavLink>
                    <NavLink
                      href="/weekly-report"
                      isMobile
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Fill Out a Report
                    </NavLink>
                    <div className="border-t border-gray-200 mt-2 pt-2">
                      <span className="block pl-3 pr-4 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Admin
                      </span>
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          window.location.href = "/company?tab=team";
                        }}
                        className="flex items-center w-full pl-3 pr-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                        data-testid="mobile-nav-team-mgmt"
                      >
                        <Users className="mr-3 h-5 w-5" />
                        Team Mgmt
                      </button>
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          window.location.href = "/company?tab=billing";
                        }}
                        className="flex items-center w-full pl-3 pr-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                        data-testid="mobile-nav-billing"
                      >
                        <CreditCard className="mr-3 h-5 w-5" />
                        Billing
                      </button>
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          window.location.href = "/company?tab=settings";
                        }}
                        className="flex items-center w-full pl-3 pr-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                        data-testid="mobile-nav-settings"
                      >
                        <Settings className="mr-3 h-5 w-5" />
                        Settings
                      </button>
                      {hasSaasReadAccess(user) && (
                        <>
                          <button
                            onClick={() => {
                              setMobileMenuOpen(false);
                              window.location.href = "/saas-settings";
                            }}
                            className="flex items-center w-full pl-3 pr-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                            data-testid="mobile-nav-saas-settings"
                          >
                            <Building2 className="mr-3 h-5 w-5" />
                            SaaS Settings
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {/* Mobile user menu */}
          <div className="pt-4 pb-3 border-t border-gray-200">
            <div className="flex items-center px-4 mb-3">
              <div className="flex-shrink-0">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={user?.profileImageUrl || ""} alt="Profile" />
                  <AvatarFallback>
                    {user?.firstName?.charAt(0) || user?.lastName?.charAt(0) || <User />}
                  </AvatarFallback>
                </Avatar>
              </div>
              <div className="ml-3">
                <div className="text-base font-medium text-gray-800">
                  {user?.firstName} {user?.lastName}
                </div>
                <div className="text-sm font-medium text-gray-500">{user?.email}</div>
              </div>
            </div>
            <div className="space-y-1">
              <button
                onClick={() => {
                  window.location.href = "/account";
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                data-testid="mobile-menu-edit-profile"
              >
                <Settings className="inline-block mr-3 h-5 w-5" />
                Edit Profile
              </button>
              <button
                onClick={() => {
                  window.location.href = "/new-features";
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                data-testid="mobile-menu-new-features"
              >
                <Sparkles className="inline-block mr-3 h-5 w-5" />
                New Features
              </button>
              {user?.isReferralPartner && (
                <button
                  onClick={() => {
                    window.location.href = "/referral-dashboard";
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                  data-testid="mobile-menu-referral-dashboard"
                >
                  <Share2 className="inline-block mr-3 h-5 w-5" />
                  Referral Dashboard
                </button>
              )}
              <button
                onClick={() => {
                  window.location.href = "/help";
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                data-testid="mobile-menu-help"
              >
                <HelpCircle className="inline-block mr-3 h-5 w-5" />
                Help & Support
              </button>
              <button
                onClick={() => {
                  window.location.href = "/report-bug";
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                data-testid="mobile-menu-report-bug"
              >
                <Bug className="inline-block mr-3 h-5 w-5" />
                Report a Bug
              </button>
              <button
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
                className="w-full text-left block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 disabled:opacity-50"
                data-testid="mobile-menu-logout"
              >
                <LogOut className="inline-block mr-3 h-5 w-5" />
                {logoutMutation.isPending ? "Logging out..." : "Logout"}
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
